import type { NarrationLine } from './types';

export type VoiceId = 'computer' | 'zoe' | 'max';

export const CHILD_VOICES: ReadonlyArray<'zoe' | 'max'> = ['zoe', 'max'] as const;

export type VoiceManifest = { zoe: string[]; max: string[] };

export const EMPTY_VOICE_MANIFEST: VoiceManifest = { zoe: [], max: [] };

export function voicePath(voice: VoiceId, line: NarrationLine): string {
  if (voice === 'zoe') {
    return `/audio/zoe/${line.id}.mp3`;
  }
  if (voice === 'max') {
    return `/audio/max/${line.id}.mp3`;
  }
  return line.audioPath;
}

export function availableVoices(id: string, manifest: VoiceManifest): VoiceId[] {
  const result: VoiceId[] = ['computer'];
  if (manifest.zoe && manifest.zoe.includes(id)) {
    result.push('zoe');
  }
  if (manifest.max && manifest.max.includes(id)) {
    result.push('max');
  }
  return result;
}

export function pickVoice(
  available: VoiceId[],
  last: VoiceId | null,
  random: () => number,
): VoiceId {
  if (available.length === 0) {
    return 'computer';
  }
  if (available.length === 1) {
    return available[0];
  }

  let candidates = available;
  if (last !== null && available.includes(last)) {
    const filtered = available.filter((v) => v !== last);
    if (filtered.length > 0) {
      candidates = filtered;
    }
  }

  const r = random();
  const clamped = Math.max(0, Math.min(r, 0.9999999999999999));
  const index = Math.floor(clamped * candidates.length);
  return candidates[Math.min(index, candidates.length - 1)];
}

export const COUNT_IDS: string[] = [
  'count-1',
  'count-2',
  'count-3',
  'count-4',
  'count-5',
  'count-6',
  'count-7',
  'count-8',
  'count-9',
  'count-10',
];

export function completeCountingVoices(manifest: VoiceManifest): VoiceId[] {
  const result: VoiceId[] = ['computer'];
  const zoeSet = new Set(manifest.zoe ?? []);
  if (COUNT_IDS.every((id) => zoeSet.has(id))) {
    result.push('zoe');
  }
  const maxSet = new Set(manifest.max ?? []);
  if (COUNT_IDS.every((id) => maxSet.has(id))) {
    result.push('max');
  }
  return result;
}

export function pickCountingVoice(
  manifest: VoiceManifest,
  previous: VoiceId | null,
  random: () => number,
): VoiceId {
  const eligible = completeCountingVoices(manifest);
  return pickVoice(eligible, previous, random);
}

export const FUEL_LINE_IDS: string[] = [
  'fuel-intro',
  'fuel-needs',
  'fuel-ready',
  'blast-off',
  'fuel-great',
];

/** Level 3 reuses count-1..10 (needs-N, live count, 3-2-1) plus its own lines. */
export const FUEL_VOICE_IDS: string[] = [...COUNT_IDS, ...FUEL_LINE_IDS];

/**
 * A child voice is eligible for Level 3 only when it has EVERY clip, so a
 * round never mixes speakers or sounds broken. Computer is always eligible.
 */
export function completeFuelVoices(manifest: VoiceManifest): VoiceId[] {
  const result: VoiceId[] = ['computer'];
  const zoeSet = new Set(manifest.zoe ?? []);
  if (FUEL_VOICE_IDS.every((id) => zoeSet.has(id))) {
    result.push('zoe');
  }
  const maxSet = new Set(manifest.max ?? []);
  if (FUEL_VOICE_IDS.every((id) => maxSet.has(id))) {
    result.push('max');
  }
  return result;
}

export function pickFuelVoice(
  manifest: VoiceManifest,
  previous: VoiceId | null,
  random: () => number,
): VoiceId {
  return pickVoice(completeFuelVoices(manifest), previous, random);
}

/** Spoken launch countdown order. */
export function fuelCountdownIds(): string[] {
  return ['count-3', 'count-2', 'count-1', 'blast-off'];
}

export function parseVoiceManifest(raw: unknown): VoiceManifest {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { zoe: [], max: [] };
  }
  const rec = raw as Record<string, unknown>;
  const parseList = (val: unknown): string[] => {
    if (!Array.isArray(val)) return [];
    const strings: string[] = [];
    const seen = new Set<string>();
    for (const item of val) {
      if (typeof item === 'string' && !seen.has(item)) {
        seen.add(item);
        strings.push(item);
      }
    }
    return strings;
  };

  return {
    zoe: parseList(rec.zoe),
    max: parseList(rec.max),
  };
}
